import React from 'react';
import { Info, AlertCircle, Smartphone, Clock, CreditCard } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export default function PraktiskInfo() {
  const sections = [
    {
      title: 'Tidsbestilling',
      icon: Clock,
      content: `
        <p>Vi benytter "Tid samme dag" (TSD). Det betyder, at du kan få en tid den dag, du har brug for det, hvis du kontakter os eller bestiller online om morgenen.</p>
        <p>Online tiderne hos lægerne bliver tilgængelige dagen før fra kl. 16:00. Du kan også ringe til os mellem kl. 08:00 og 12:00.</p>
      `
    },
    {
      title: 'Akut behov',
      icon: AlertCircle,
      content: `
        <p>Ved akut opstået sygdom uden for vores åbningstid, bedes du kontakte 1813.</p>
        <p>I vores åbningstid kan du kontakte klinikken direkte på vores hovednummer. Ved livstruende tilstande ring altid 112.</p>
      `
    },
    {
      title: 'MinLæge App',
      icon: Smartphone,
      content: `
        <p>Vi anbefaler alle vores patienter at hente appen "Min Læge". Her kan du:</p>
        <ul>
          <li>Se dine aftaler</li>
          <li>Forny recepter</li>
          <li>Skrive korte beskeder til lægen</li>
          <li>Se dine prøvesvar</li>
          <li>Starte videokonsultation (efter aftale)</li>
        </ul>
      `
    },
    {
      title: 'Betaling',
      icon: CreditCard,
      content: `
        <p>Vi modtager betalingskort i klinikken.</p>
        <p>Husk altid dit gule sundhedskort (eller appen), da det skal scannes ved ankomst.</p>
      `
    }
  ];

  return (
    <main className="pt-32 pb-24">
      <Helmet>
        <title>Praktisk Information & Tidsbestilling | Hellerup Lægehus</title>
        <meta name="description" content="Læs om tidsbestilling (Tid Samme Dag), akut behov, MinLæge appen og betaling hos Hellerup Lægehus. Find svar på dine praktiske spørgsmål her." />
        <meta property="og:title" content="Praktisk Information & Tidsbestilling | Hellerup Lægehus" />
        <meta property="og:description" content="Læs om tidsbestilling (Tid Samme Dag), akut behov, MinLæge appen og betaling hos Hellerup Lægehus. Find svar på dine praktiske spørgsmål her." />
        <html lang="da-DK" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-20">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-[1px] w-12 bg-[#4b717a]" />
            <span className="text-[#4b717a] text-xs tracking-[0.4em] uppercase font-bold">Praktisk Information</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-sans font-medium text-black mb-8 leading-tight tracking-tight">Godt at vide</h1>
          <p className="text-slate-600 text-lg max-w-2xl leading-relaxed">
            Her har vi samlet de mest nødvendige informationer for at gøre dit besøg i klinikken så smidigt som muligt.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {sections.map((section) => (
            <div key={section.title} className="bg-white p-10 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="text-[#4b717a]">
                  <section.icon size={24} />
                </div>
                <h3 className="text-2xl font-sans font-medium text-black">{section.title}</h3>
              </div>
              <div 
                className="text-slate-600 leading-relaxed prose prose-slate prose-sm max-w-none
                  prose-p:mb-4 prose-ul:list-disc prose-ul:ml-4 prose-li:mb-2"
                dangerouslySetInnerHTML={{ __html: section.content }}
              />
            </div>
          ))}
        </div>

        <div className="mt-20 bg-[#4b717a] p-10 md:p-16 text-white text-center">
          <h2 className="text-3xl font-sans font-medium mb-6">Har du stadig spørgsmål?</h2>
          <p className="text-slate-100 mb-10 max-w-xl mx-auto">
            Du er altid velkommen til at ringe til os i telefontiden, hvis der er noget du er i tvivl om.
          </p>
          <a 
            href="tel:70602202" 
            className="inline-flex items-center gap-3 bg-white text-[#4b717a] px-8 py-4 font-bold tracking-widest uppercase text-xs hover:bg-slate-50 transition-colors"
          >
            Ring til os: 70 60 22 02
          </a>
        </div>
      </div>
    </main>
  );
}
